/* eslint-disable react-hooks/set-state-in-effect */
import React, { useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { DataService } from "../../services/dataService";
import type { IKompetenzItem } from "../../models/IKompetenzItem";
import type { IDokumentItem } from "../../models/IDokumentItem";
import type { IDataItem } from "../../models/IDataItem";
import type { IProjektItem } from "../../models/IProjektItem";
import { EditRegular, TableRegular } from "@fluentui/react-icons";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  flattenTree_unstable,
  FlatTree,
  FlatTreeItem,
  Option,
  Radio,
  RadioGroup,
  Textarea,
  tokens,
  TreeItemLayout,
} from "@fluentui/react-components";
import Dokumente from "./dokumente";
import { useQualifikationsprofilStyles as useStyles } from "../../utils/styles";

interface QualifikationsprofilProps {
  canEdit?: boolean;
  projekt: IProjektItem;
  dokumente: IDokumentItem[];
  onProjektUpdated: () => Promise<void>;
  onDokumenteUpdated: () => Promise<void>;
}

const Qualifikationsprofil: React.FC<QualifikationsprofilProps> = ({
  canEdit = false,
  projekt,
  dokumente,
  onProjektUpdated,
  onDokumenteUpdated,
}) => {
  const panelTopRef = useRef<HTMLDivElement>(null);
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [color, setColor] = useState("");
  const [kompetenzen, setKompetenzen] = useState<IKompetenzItem[]>([]);
  const [kompetenzDrafts, setKompetenzDrafts] = useState<
    Record<string, Partial<Pick<IKompetenzItem, "kompetenz" | "deaktiviert">>>
  >({});
  const [qualProfil, setQualProfil] = useState<string>(
    projekt.qualifikationsprofil ?? "",
  );
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rasterSource, setRasterSource] = useState<"standard" | "uebernahme">(
    "standard",
  );
  const [rasterProjekte, setRasterProjekte] = useState<IDataItem[]>([]);
  const [rasterProjektId, setRasterProjektId] = useState("");
  const [isLoadingRasterProjekte, setIsLoadingRasterProjekte] = useState(false);
  const [isLoadingRaster, setIsLoadingRaster] = useState(false);

  const [updateBemerkung, setUpdateBemerkung] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const scrollToTopOfPanel = () => {
    setTimeout(() => {
      if (panelTopRef.current) {
        // First, use scrollIntoView immediately
        panelTopRef.current.scrollIntoView({
          behavior: "auto",
          block: "start",
        });

        // Then find and scroll the scrollable parent to ensure we're at the top
        let element = panelTopRef.current.parentElement;
        while (element) {
          const overflowY = window.getComputedStyle(element).overflowY;
          const isScrollable = overflowY === "scroll" || overflowY === "auto";
          if (isScrollable && element.scrollHeight > element.clientHeight) {
            element.scrollTop = 0;
            break;
          }
          element = element.parentElement;
        }
      }
    }, 150);
  };

  const kompetenzTreeItems = useMemo(() => {
    if (kompetenzen.length === 0) {
      return [];
    }

    const compareParagraph = (left: IKompetenzItem, right: IKompetenzItem) => {
      const leftParagraph = (left.paragraph ?? "").toString();
      const rightParagraph = (right.paragraph ?? "").toString();

      return leftParagraph.localeCompare(rightParagraph, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    };

    const groupedKompetenzen = new Map<string, IKompetenzItem[]>();

    kompetenzen.forEach((kompetenz) => {
      const parentKey = String(kompetenz.parentId ?? 0);
      const siblings = groupedKompetenzen.get(parentKey) ?? [];
      siblings.push(kompetenz);
      groupedKompetenzen.set(parentKey, siblings);
    });

    type KompetenzTreeItem = {
      value: string;
      itemType: "branch" | "leaf";
      children: React.ReactNode;
      parentValue?: string | number;
      "aria-level"?: number;
      "aria-setsize"?: number;
      "aria-posinset"?: number;
      subtree?: KompetenzTreeItem[];
    };

    const buildTree = (parentKey: string): KompetenzTreeItem[] => {
      const children = (groupedKompetenzen.get(parentKey) ?? []).sort(
        compareParagraph,
      );

      return children.map((kompetenz) => {
        const currentKey = String(kompetenz.id);
        const subtree = buildTree(currentKey);
        const isLeaf = subtree.length === 0;
        const textDecoration =
          !editMode && kompetenz.deaktiviert ? "line-through" : undefined;

        const isParent =
          subtree.length > 0 ||
          (subtree.length === 0 && kompetenz.parentId === null);

        return {
          value: currentKey,
          itemType: isParent ? "branch" : "leaf",
          children: (
            <TreeItemLayout
              style={{ width: "100%" }}
              main={{ style: { flex: "1 1 0", minWidth: 0 } }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 120px",
                  alignItems: "center",
                  width: "100%",
                  minWidth: 0,
                  gap: "16px",
                  fontWeight: isParent ? 700 : 400,
                }}
              >
                {editMode && canEdit && isLeaf ? (
                  <div
                    style={{ display: "grid", gap: "4px", minWidth: 0 }}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <Textarea
                      className={styles.input}
                      aria-label={`Kompetenz ${kompetenz.paragraph || kompetenz.id}`}
                      value={
                        kompetenzDrafts[currentKey]?.kompetenz ??
                        kompetenz.kompetenz ??
                        ""
                      }
                      rows={2}
                      resize="vertical"
                      disabled={isSaving}
                      onChange={(_, data) =>
                        setKompetenzDrafts((drafts) => ({
                          ...drafts,
                          [currentKey]: {
                            ...drafts[currentKey],
                            kompetenz: data.value,
                          },
                        }))
                      }
                    />
                    <Checkbox
                      label="Deaktiviert"
                      aria-label={`Kompetenz ${kompetenz.paragraph || kompetenz.id} deaktiviert`}
                      checked={
                        kompetenzDrafts[currentKey]?.deaktiviert ??
                        kompetenz.deaktiviert
                      }
                      disabled={isSaving}
                      onChange={(_, data) =>
                        setKompetenzDrafts((drafts) => ({
                          ...drafts,
                          [currentKey]: {
                            ...drafts[currentKey],
                            deaktiviert: data.checked === true,
                          },
                        }))
                      }
                    />
                  </div>
                ) : (
                  <span
                    style={{
                      minWidth: 0,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      textDecoration,
                    }}
                  >
                    {kompetenz.kompetenz || "Unbenannte Kompetenz"}
                    {isLeaf && kompetenz.deaktiviert && (
                      <span style={{ color: tokens.colorNeutralForeground2 }}>
                        {" (deaktiviert)"}
                      </span>
                    )}
                  </span>
                )}
                <span
                  style={{
                    textAlign: "right",
                    color: tokens.colorNeutralForeground2,
                    whiteSpace: "nowrap",
                    fontWeight: isParent ? 700 : 400,
                    textDecoration,
                  }}
                >
                  {kompetenz.paragraph || ""}
                </span>
              </div>
            </TreeItemLayout>
          ),
          ...(isParent ? { subtree } : {}),
        };
      });
    };

    return flattenTree_unstable(buildTree("0"));
  }, [kompetenzen, editMode, canEdit, kompetenzDrafts, styles.input, isSaving]);

  const renderedKompetenzTreeItems = React.useMemo(
    () =>
      kompetenzTreeItems.map((item) => (
        <FlatTreeItem
          key={String(item.value)}
          style={{ width: "100%" }}
          value={String(item.value)}
          itemType={item.itemType}
          parentValue={item.parentValue}
          aria-level={Number(item["aria-level"] ?? 1)}
          aria-setsize={Number(item["aria-setsize"] ?? 1)}
          aria-posinset={Number(item["aria-posinset"] ?? 1)}
        >
          {item.children}
        </FlatTreeItem>
      )),
    [kompetenzTreeItems],
  );

  React.useEffect(() => {
    if (
      !editMode ||
      !canEdit ||
      rasterSource !== "uebernahme" ||
      renderedKompetenzTreeItems.length > 0 ||
      !dataService ||
      !projekt.itemId
    ) {
      return;
    }

    let active = true;
    setIsLoadingRasterProjekte(true);
    setErrorMessage("");

    const fetchRasterProjekte = async () => {
      try {
        const response = await dataService.getProjekteRasterAuswahl(
          projekt.itemId,
        );
        if (active) setRasterProjekte(response);
      } catch (error) {
        console.error("Failed to load Kompetenzraster source projects:", error);
        if (active) {
          setErrorMessage("Die Projektauswahl konnte nicht geladen werden.");
          setColor("red");
          scrollToTopOfPanel();
        }
      } finally {
        if (active) setIsLoadingRasterProjekte(false);
      }
    };

    void fetchRasterProjekte();
    return () => {
      active = false;
    };
  }, [
    editMode,
    canEdit,
    rasterSource,
    renderedKompetenzTreeItems.length,
    dataService,
    projekt.itemId,
  ]);

  const loadKompetenzraster = async () => {
    setErrorMessage("");
    if (!canEdit || !dataService || !projekt.itemId) {
      setErrorMessage("Das Kompetenzraster kann derzeit nicht geladen werden.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }

    const sourceProjektId =
      rasterSource === "standard" ? 0 : Number(rasterProjektId);
    if (
      rasterSource === "uebernahme" &&
      (!Number.isSafeInteger(sourceProjektId) || sourceProjektId <= 0)
    ) {
      setErrorMessage("Bitte wählen Sie ein Projekt aus.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }

    setIsLoadingRaster(true);
    try {
      const created = await dataService.createKompetenzraster(
        projekt.itemId,
        sourceProjektId,
      );
      if (!created) {
        throw new Error("Kompetenzraster creation was not successful.");
      }
      const response = await dataService.getProjektKompetenzen(projekt.itemId);
      setKompetenzen(response);
    } catch (error) {
      console.error("Failed to load Kompetenzraster:", error);
      setErrorMessage("Das Kompetenzraster konnte nicht geladen werden.");
      setColor("red");
      scrollToTopOfPanel();
    } finally {
      setIsLoadingRaster(false);
    }
  };

  React.useEffect(() => {
    if (!dataService) return;
    if (!projekt.itemId) return;
    const fetchItems = async () => {
      const response = await dataService.getProjektKompetenzen(projekt.itemId);
      if (response) setKompetenzen(response);
    };

    fetchItems();
  }, [dataService, projekt.itemId, editMode]);

  const loadXlsxData = async () => {
    if (!dataService) return;
    if (!projekt.itemId) return;
    const base64String = await dataService.getProjektKompetenzenXlsx(
      projekt.itemId,
    );
    const binaryString = window.atob(base64String);
    const binaryLen = binaryString.length;
    const bytes = new Uint8Array(binaryLen);
    for (let i = 0; i < binaryLen; i++) {
      const ascii = binaryString.charCodeAt(i);
      bytes[i] = ascii;
    }
    const blob = new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(
      new Blob([blob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "Kompetenzraster_" + projekt.bezeichnung + ".xlsx";
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const saveProjekt = async () => {
    if (isSaving) return;
    setErrorMessage("");
    if (!canEdit || !dataService) {
      setErrorMessage("Die Daten können derzeit nicht gespeichert werden.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }
    if (qualProfil === undefined || qualProfil === null) {
      setErrorMessage("Bitte füllen Sie alle erforderlichen Felder aus.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }
    setIsSaving(true);
    try {
      const result = await dataService.addUpdateProjekt({
        ...projekt,
        qualifikationsprofil: qualProfil,
        updateBemerkung: updateBemerkung,
        kategorie: "Qualifikationsprofil",
      });
      if (result === false) {
        throw new Error("Project update was not successful.");
      }

      const parentIds = new Set(
        kompetenzen.map((kompetenz) => String(kompetenz.parentId ?? 0)),
      );
      const changedKompetenzen = kompetenzen.filter((kompetenz) => {
        const draft = kompetenzDrafts[kompetenz.id];
        return (
          !parentIds.has(String(kompetenz.id)) &&
          ((draft?.kompetenz !== undefined &&
            draft.kompetenz !== kompetenz.kompetenz) ||
            (draft?.deaktiviert !== undefined &&
              draft.deaktiviert !== kompetenz.deaktiviert))
        );
      });

      for (const kompetenz of changedKompetenzen) {
        const updated = await dataService.updateKompetenz({
          ...kompetenz,
          ...kompetenzDrafts[kompetenz.id],
        });
        if (updated === false) {
          throw new Error(
            `Kompetenz ${kompetenz.id} update was not successful.`,
          );
        }
      }

      if (changedKompetenzen.length > 0) {
        const response = await dataService.getProjektKompetenzen(
          projekt.itemId,
        );
        setKompetenzen(response);
      }
      await onProjektUpdated();
      setKompetenzDrafts({});
      setUpdateBemerkung("");
      setErrorMessage("Daten erfolgreich gespeichert");
      setColor("green");
      scrollToTopOfPanel();
      setTimeout(() => {
        setErrorMessage("");
        setEditMode(false);
        setIsSaving(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to save project or competencies:", error);
      setErrorMessage(
        "Fehler beim Speichern der Projektdaten oder Kompetenzen. Bereits gespeicherte Änderungen bleiben erhalten. Bitte erneut versuchen.",
      );
      setColor("red");
      scrollToTopOfPanel();
      setIsSaving(false);
    }
  };

  const _saveClicked = () => {
    setIsDialogOpen(true);
  };

  const renderReadMode = () => (
    <>
      <div className="verticalLayout">
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>
              Qualifikationsprofil: {projekt.bezeichnung}
            </h3>
            {canEdit && (
              <Button
                appearance="secondary"
                icon={<EditRegular />}
                onClick={() => {
                  setQualProfil(projekt.qualifikationsprofil ?? "");
                  setKompetenzDrafts({});
                  setEditMode(true);
                }}
              >
                Bearbeitungsmodus starten
              </Button>
            )}
          </div>
          <div>
            <p>{projekt.qualifikationsprofil}</p>
          </div>
        </section>
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Kompetenzraster</h3>
            {renderedKompetenzTreeItems.length > 0 && (
              <Button
                appearance="secondary"
                icon={<TableRegular />}
                onClick={() => {
                  loadXlsxData();
                }}
              >
                In Excel öffnen
              </Button>
            )}
          </div>
          <div>
            {renderedKompetenzTreeItems.length > 0 ? (
              <FlatTree aria-label="Kompetenzraster" style={{ width: "100%" }}>
                {renderedKompetenzTreeItems}
              </FlatTree>
            ) : (
              <p
                style={{
                  margin: 0,
                  color: tokens.colorNeutralForeground3,
                }}
              >
                Noch kein Kompetenzraster hinzugefügt.
              </p>
            )}
          </div>
        </section>
        <Dokumente projektId={projekt.itemId} dokumente={dokumente} />
      </div>
    </>
  );

  const renderEditMode = () => (
    <>
      <div ref={panelTopRef}>
        {errorMessage && (
          <div
            style={{
              fontSize: "16px",
              marginBottom: "30px",
              maxWidth: "500px",
              color: color,
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
      <div className="verticalLayout">
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>
              Qualifikationsprofil: {projekt.bezeichnung}
            </h3>
            <div style={{ display: "flex", gap: "8px" }}>
              {canEdit && (
                <Button
                  appearance="primary"
                  disabled={isSaving}
                  onClick={() => {
                    _saveClicked();
                  }}
                >
                  Speichern
                </Button>
              )}
              <Button
                appearance="secondary"
                onClick={() => {
                  setErrorMessage("");
                  setEditMode(false);
                }}
              >
                Abbrechen
              </Button>
            </div>
          </div>
          <div>
            <Textarea
              className={styles.input}
              id="qualProfil"
              value={qualProfil}
              disabled={isSaving}
              autoFocus
              rows={2}
              onChange={(e) => setQualProfil(e.target.value)}
            />
          </div>
        </section>
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Kompetenzraster</h3>
            {renderedKompetenzTreeItems.length > 0 && (
              <Button
                appearance="secondary"
                icon={<TableRegular />}
                onClick={() => {
                  loadXlsxData();
                }}
              >
                In Excel öffnen
              </Button>
            )}
          </div>
          <div>
            {renderedKompetenzTreeItems.length > 0 ? (
              <FlatTree aria-label="Kompetenzraster" style={{ width: "100%" }}>
                {renderedKompetenzTreeItems}
              </FlatTree>
            ) : (
              <>
                {canEdit && (
                  <div className={styles.rasterSelector}>
                    <div>
                      <div className={styles.label}>
                        Kompetenzraster auswählen
                      </div>
                      <div className={styles.rasterHint}>
                        Standard verwenden oder aus einem Projekt übernehmen
                      </div>
                    </div>
                    <RadioGroup
                      aria-label="Kompetenzraster auswählen"
                      layout="horizontal"
                      className={styles.rasterSource}
                      value={rasterSource}
                      disabled={isLoadingRaster}
                      onChange={(_, data) => {
                        if (
                          data.value === "standard" ||
                          data.value === "uebernahme"
                        ) {
                          setRasterSource(data.value);
                          setErrorMessage("");
                        }
                      }}
                    >
                      <Radio value="standard" label="Standard" />
                      <Radio value="uebernahme" label="Übernahme" />
                    </RadioGroup>
                    <Dropdown
                      aria-label="Projekt für die Rasterübernahme"
                      className={styles.rasterProject}
                      placeholder={
                        isLoadingRasterProjekte && rasterSource === "uebernahme"
                          ? "Projekte werden geladen ..."
                          : "Projekt auswählen ..."
                      }
                      disabled={
                        rasterSource !== "uebernahme" ||
                        isLoadingRasterProjekte ||
                        isLoadingRaster
                      }
                      value={
                        rasterProjekte.find((p) => p.value === rasterProjektId)
                          ?.text ?? ""
                      }
                      selectedOptions={rasterProjektId ? [rasterProjektId] : []}
                      onOptionSelect={(_, data) =>
                        setRasterProjektId(data.optionValue ?? "")
                      }
                    >
                      {rasterProjekte.map((projekt) => (
                        <Option
                          key={projekt.value}
                          value={projekt.value}
                          text={projekt.text}
                        >
                          {projekt.text}
                        </Option>
                      ))}
                    </Dropdown>
                    <Button
                      appearance="primary"
                      disabled={
                        !dataService ||
                        isLoadingRaster ||
                        (rasterSource === "uebernahme" &&
                          (isLoadingRasterProjekte || !rasterProjektId))
                      }
                      onClick={() => void loadKompetenzraster()}
                    >
                      {isLoadingRaster
                        ? "Raster wird geladen ..."
                        : "Raster laden"}
                    </Button>
                  </div>
                )}
                <p
                  style={{
                    margin: 0,
                    color: tokens.colorNeutralForeground3,
                  }}
                >
                  Noch kein Kompetenzraster hinzugefügt.
                </p>
              </>
            )}
          </div>
        </section>
        <Dokumente
          projektId={projekt.itemId}
          dokumente={dokumente}
          editable={canEdit}
          onDokumenteUpdated={onDokumenteUpdated}
        />
      </div>
      <Dialog
        open={isDialogOpen}
        onOpenChange={(_e, data) => setIsDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogTitle style={{ marginBottom: "10px" }}>
            Änderungsbegründung
          </DialogTitle>
          <DialogContent>
            <div className={styles.label}>Begründung der Änderung:</div>
            <div className={styles.value}>
              <Textarea
                className={styles.input}
                id="bemerkung"
                value={updateBemerkung}
                autoFocus
                rows={3}
                onChange={(e) => setUpdateBemerkung(e.target.value)}
              />
            </div>
          </DialogContent>
          <DialogActions style={{ marginTop: "10px" }}>
            <Button
              appearance="primary"
              disabled={!updateBemerkung || isSaving}
              onClick={() => {
                setIsDialogOpen(false);
                void saveProjekt();
              }}
            >
              Speichern
            </Button>
            <Button
              appearance="secondary"
              onClick={() => setIsDialogOpen(false)}
            >
              Abbrechen
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </>
  );

  return editMode ? renderEditMode() : renderReadMode();
};

export default Qualifikationsprofil;
